import { Button, ButtonProps, FormControl, FormErrorMessage, FormLabel, Input, Textarea } from '@chakra-ui/react';
import { FC, Fragment, ReactNode } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmailSourceType } from '@/enums/ProfileContacts';
import { IMessageInfo, sendProfileEmail } from '@/fetchers/profile-contact';
import { useStores } from '@/hooks/useStores';
import { useMutation } from '@/lib/datx-jsonapi-react';
import { ThinProfile } from '@/resources/ThinProfile';

export interface IProfileContactFormValues {
  name: string;
  email: string;
  message?: string;
}

export interface IProfileContactFormProps {
  lectureId?: string;
  eventId?: string;
  profile: ThinProfile;
  onSubmitSuccess: () => void;
  onSubmitFailure: () => void;
  defaultValues: IProfileContactFormValues;
  children: ReactNode;
  source: EmailSourceType;
}

/**
 * Multi-part form component for speaker contact form
 *
 * @example
 *
 * <SpeakerContactForm>
 *   <SpeakerContactFormFieldset />
 *   <SpeakerContactFormSubmit/>
 * </SpeakerContactForm>
 */
export const SpeakerContactForm: FC<IProfileContactFormProps> = ({
  profile,
  lectureId,
  eventId,
  onSubmitSuccess,
  onSubmitFailure,
  children,
  source,
  defaultValues,
}) => {
  const methods = useForm<IProfileContactFormValues>({
    defaultValues,
  });

  const { handleSubmit } = methods;

  const [mutate] = useMutation(sendProfileEmail, {
    onSuccess: () => {
      onSubmitSuccess?.();
    },
    onFailure: () => {
      onSubmitFailure?.();
    },
  });

  const onSubmit = async (formData) => {
    const message_info: IMessageInfo = {
      sender_name: formData.name,
      sender_email: formData.email,
      recipient_email: profile.email,
      message: formData.message,
    };

    mutate({
      lectureId,
      eventId,
      message_info,
      source,
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>{children}</form>
    </FormProvider>
  );
};
export const SpeakerContactFormFieldset = () => {
  const { t } = useTranslation();

  const formContext = useFormContext();

  const {
    register,
    formState: { errors },
  } = formContext;

  return (
    <Fragment>
      <FormControl isInvalid={Boolean(errors.name)}>
        <FormLabel>{t('contactForm.name.label')}</FormLabel>
        <Input {...register('name', { required: true })} placeholder={t('contactForm.name.placeholder')} />
        <FormErrorMessage>{t('contactForm.name.required')}</FormErrorMessage>
      </FormControl>

      <FormControl mt={{ base: 2, md: 4 }} isInvalid={Boolean(errors.email)}>
        <FormLabel>{t('contactForm.email.label')}</FormLabel>
        <Input
          {...register('email', {
            required: true,
            pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          })}
          placeholder={t('contactForm.email.placeholder')}
        />
        <FormErrorMessage>{t(`contactForm.email.${errors?.email?.type}`)}</FormErrorMessage>
      </FormControl>

      <FormControl mt={{ base: 4, md: 8 }} isInvalid={Boolean(errors.message)}>
        <FormLabel>{t('contactForm.message.label')}</FormLabel>
        <Textarea
          {...register('message', { required: true })}
          placeholder={t('contactForm.message.placeholder')}
          minH="200px"
        />
        <FormErrorMessage>{t('contactForm.message.required')}</FormErrorMessage>
      </FormControl>
    </Fragment>
  );
};

export interface ISpeakerContactFormSubmitProps extends ButtonProps {
  children: ReactNode;
}

export const SpeakerContactFormSubmit: FC<ISpeakerContactFormSubmitProps> = ({ children, ...rest }) => {
  const formContext = useFormContext();

  if (!formContext) {
    throw new Error(
      'useFormContext: `context` is undefined. Seems you forgot to wrap modal components in `<FormProvider />`'
    );
  }

  return (
    <Button isLoading={formContext.formState.isSubmitting} type="submit" {...rest}>
      {children}
    </Button>
  );
};
